import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/button'
import XeroLoginButton from './XeroLoginButton'
import { useCompleteXeroSignIn } from '@/services/api/auth/useXeroSignIn'

/**
 * Redirect target registered on the Xero app for Sign in with Xero. Posts
 * code + state to the API, which resolves/creates the account and logs in.
 */
export default function XeroSignInCallbackPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const started = useRef(false)
  const { action: complete } = useCompleteXeroSignIn()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Codes are single-use; guard against StrictMode's double effect.
    if (started.current) return
    started.current = true

    const xeroError = searchParams.get('error')
    const code = searchParams.get('code')
    const state = searchParams.get('state')

    if (xeroError || !code || !state) {
      setError(
        xeroError === 'access_denied'
          ? 'Xero sign-in was cancelled.'
          : 'Xero did not complete the sign-in. Please try again.',
      )
      return
    }

    complete({ code, state })
      .then(() => navigate('/', { replace: true }))
      .catch((e) =>
        setError(
          e?.response?.data?.message ??
            'We could not sign you in with Xero. Please try again.',
        ),
      )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="pf-auth grid min-h-screen px-4">
      <Card className="w-full max-w-md place-self-center">
        <CardHeader>
          <CardTitle>Sign in with Xero</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {error ? (
            <>
              <p className="text-sm text-muted-foreground">{error}</p>
              <div className="grid gap-3">
                <XeroLoginButton label="Try again with Xero" />
                <Button
                  variant="outline"
                  onClick={() => navigate('/login', { replace: true })}
                >
                  Back to sign in
                </Button>
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Signing you in…</p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
